import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import { type HospitalDataContextType, useHospitalData } from "../contexts/HospitalDataProvider";
import { findScheduleSlot, type ScheduleSlot } from "../utils/departmentWeeks";
import type { ShiftPerson, SpecificPreference, TransformedShift, WeekData } from "../utils/models";
import { getFirstLoadedDate, panelWeeksOf, panelWindowOf } from "../utils/panelWeeks";
import { daysInRange, shiftPersonOf, toIsoDate, weekStartsBetween } from "../utils/scheduleWeeks";
import { transformedShiftsOf } from "../utils/viewerShifts";
import { scheduleWeekQuery } from "./scheduleCache";
import { useLastNameFirst } from "./useNameFormat";
import { departmentRosterQueryOptions } from "./useNurses";
import { useRosterLookup } from "./useRosterLookup";
import { useScheduleDays } from "./useScheduleDays";
import { useScheduleCacheEnabled, useScheduleStartDay } from "./useScheduleWeekRules";

// One array, so a viewer who doesn't work here doesn't get a new one on every render.
const NO_SHIFTS: TransformedShift[] = [];
const NO_TIME_OFF: SpecificPreference[] = [];
// The old list is loaded before any panel opens.
const notLoading = (): boolean => false;

export type ScheduleWeeks = {
	/**
	 * The weeks a panel reads, in the old list's shape (`weeks[w].weeklyShifts[s].days[d]`). Flag off, every loaded
	 * week of the old list, as before. Flag on, the Sunday week holding the date and one on each side, from the cache.
	 * A position only means something inside these weeks, so a panel keeps a date and looks its position up here.
	 */
	weeks: WeekData[];
	/** Where `shiftType` on `date` sits in `weeks`, or `undefined` when it has no staffing row there. */
	slotOf: (date: DateTime, shiftType: string) => ScheduleSlot | undefined;
	/** The first and last day a panel's date picker can reach; undefined while unknown and with no schedule. */
	firstDate: DateTime | undefined;
	lastDate: DateTime | undefined;
	/**
	 * The people the weeks' shifts name, and one of them by id: flag off, the old list's nurses; flag on, the roster's
	 * once it's in and the schedule's own people until then, so the weeks don't wait for the roster.
	 */
	people: ShiftPerson[];
	getPerson: (id: number) => ShiftPerson | undefined;
	/**
	 * Every time-off and preference entry overlapping the weeks, once each, as the schedule returns them: what the
	 * weeks' time off is read from until the roster loads. Empty with the flag off, where the roster holds them.
	 */
	timeOff: SpecificPreference[];
	/** The weeks and the roster: what a panel that reads whole nurses waits for. */
	isLoading: boolean;
	isError: boolean;
	/** The weeks alone, named by the schedule's people. */
	isScheduleLoading: boolean;
	isScheduleError: boolean;
	/** Whether the weeks holding `from` through `to`, inside the weeks above, are still loading: what a screen drawing only those days waits for. */
	isLoadingBetween: (from: DateTime, to: DateTime) => boolean;
	refetch: () => void;
	/**
	 * The signed-in person's own shifts and time off, for their one-week calendar: flag off, every loaded week of the old
	 * list as before; flag on, `weeks`. Empty when they don't work in this department.
	 */
	viewerShifts: HospitalDataContextType["transformedShifts"];
};

/**
 * The schedule weeks a panel reads around `date`: the one place a panel's schedule comes from. With
 * `staffing_schedule_cache` on it reads the cache, so a panel opens on any day of the schedule; with it off it reads
 * the old list, exactly as the panels did.
 *
 * A draft's edits patch the cached day, so in draft mode it reads the cache too. Every hook runs in both modes (the flag can turn on while a panel is open); with the cache off `useScheduleDays`
 * asks for nothing.
 */
export const useScheduleWeeks = (date: DateTime): ScheduleWeeks => {
	const { hospitalData, latestDateInWeeksData, transformedShifts: viewerShifts, getNurseById } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const isCacheEnabled = useScheduleCacheEnabled();
	const lastNameFirst = useLastNameFirst();
	const { from, to } = panelWindowOf(date);
	const cached = useScheduleDays(from, to);
	const roster = useRosterLookup(chosenDepartment.department_id);
	const { userInfo } = useAuth();
	const viewerNurseId = userInfo.nurses_info?.find((nurseInfo) => nurseInfo.departments.includes(chosenDepartment.department_id))?.nurse.id;
	const viewerNurse = viewerNurseId === undefined ? undefined : roster.getNurseById(viewerNurseId);

	const schedulePeople = useMemo(
		() => new Map(Object.values(cached.nurses).map((person) => [person.id, shiftPersonOf(person, chosenDepartment.jobs)])),
		[cached.nurses, chosenDepartment.jobs],
	);
	// The roster once it's in, so the weeks then show exactly whom they showed before the cache.
	const getCachedPerson = useCallback(
		(id: number): ShiftPerson | undefined => (roster.roster ? roster.getNurseById(id) : schedulePeople.get(id)),
		[roster.roster, roster.getNurseById, schedulePeople],
	);
	const cachedPeople = useMemo(() => roster.roster ?? [...schedulePeople.values()], [roster.roster, schedulePeople]);
	// A several-day vacation is listed on each of its days.
	const cachedTimeOff = useMemo(
		() => [...new Map(cached.days.flatMap((day) => day.timeOff).map((preference) => [preference.id, preference])).values()],
		[cached.days],
	);
	// Built apart from the old list, so a reload of the old list leaves the cached weeks the same objects.
	const cachedWeeks = useMemo(
		() => (isCacheEnabled ? panelWeeksOf(cached.days, getCachedPerson, lastNameFirst) : []),
		[isCacheEnabled, cached.days, getCachedPerson, lastNameFirst],
	);
	const weeks = isCacheEnabled ? cachedWeeks : hospitalData.weeks;
	const slotOf = useCallback((slotDate: DateTime, shiftType: string) => findScheduleSlot(weeks, slotDate, shiftType), [weeks]);
	const refetch = useCallback(() => {
		cached.refetch();
		roster.refetch();
	}, [cached.refetch, roster.refetch]);
	const loadedFirstDate = useMemo(() => getFirstLoadedDate(hospitalData.weeks), [hospitalData.weeks]);
	const cachedViewerShifts = useMemo(
		() => (viewerNurse ? transformedShiftsOf(cachedWeeks, viewerNurse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes) : NO_SHIFTS),
		[cachedWeeks, viewerNurse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	if (!isCacheEnabled) {
		// The provider loads before any panel opens and reloads itself, so there is nothing here to wait for or retry.
		return {
			weeks,
			slotOf,
			firstDate: loadedFirstDate,
			lastDate: latestDateInWeeksData,
			people: hospitalData.nurses,
			getPerson: getNurseById,
			timeOff: NO_TIME_OFF,
			isLoading: false,
			isError: false,
			isScheduleLoading: false,
			isScheduleError: false,
			isLoadingBetween: notLoading,
			refetch: () => undefined,
			viewerShifts,
		};
	}
	return {
		weeks,
		slotOf,
		// DEFAULT: a department with no schedule (null) can't move the picker, the same as one not known yet.
		firstDate: cached.scheduleStartDate ?? undefined,
		lastDate: cached.scheduleEndDate ?? undefined,
		people: cachedPeople,
		getPerson: getCachedPerson,
		timeOff: cachedTimeOff,
		isLoading: cached.isLoading || roster.isLoading,
		// A failed background refetch keeps the loaded roster, so only a roster that never arrived fails the view.
		isError: cached.isError || (roster.isError && roster.roster === undefined),
		isScheduleLoading: cached.isLoading,
		isScheduleError: cached.isError,
		isLoadingBetween: cached.isLoadingBetween,
		refetch,
		viewerShifts: cachedViewerShifts,
	};
};

/**
 * The same weeks `useScheduleWeeks` reads, for a date only known when an action runs (a warning check, an approval).
 * Flag off, the old list; flag on, the date's Sunday week and one on each side from the cache, fetched when they
 * aren't there; in draft mode a cached week isn't asked again, since it holds the draft's edits. Rejects when a week
 * or the roster fails to load.
 */
export const useFetchScheduleWeeks = (): ((date: DateTime) => Promise<WeekData[]>) => {
	const { hospitalData } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const isCacheEnabled = useScheduleCacheEnabled();
	const { isDraftMode } = useDraftMode();
	const startDay = useScheduleStartDay();
	const queryClient = useQueryClient();
	const lastNameFirst = useLastNameFirst();

	return useCallback(
		async (date: DateTime) => {
			if (!isCacheEnabled) return hospitalData.weeks;
			const departmentId = chosenDepartment.department_id;
			const { from, to } = panelWindowOf(date);
			const [roster, scheduleWeeks] = await Promise.all([
				queryClient.ensureQueryData(departmentRosterQueryOptions(departmentId)),
				Promise.all(
					weekStartsBetween(from, to, startDay).map((weekStart) =>
						queryClient.fetchQuery({
							...scheduleWeekQuery(departmentId, weekStart),
							...(isDraftMode && { staleTime: Number.POSITIVE_INFINITY }),
						}),
					),
				),
			]);
			const nursesById = new Map(roster.map((nurse) => [nurse.id, nurse]));
			return panelWeeksOf(daysInRange(scheduleWeeks, toIsoDate(from), toIsoDate(to)), (id) => nursesById.get(id), lastNameFirst);
		},
		[isCacheEnabled, isDraftMode, hospitalData.weeks, chosenDepartment.department_id, queryClient, startDay, lastNameFirst],
	);
};

export type ShiftDayWeeks = {
	/** Weeks holding the shift's day, as `useScheduleWeeks` reads them. */
	weeks: WeekData[];
	isLoading: boolean;
	isError: boolean;
	/**
	 * `weeks` once they're in; while they load, or after they failed, fetches them, so an action never waits on a
	 * disabled button. Rejects when a week fails to load.
	 */
	loadWeeks: () => Promise<WeekData[]>;
	/** True while `loadWeeks` fetches: the action that asked has started. */
	isLoadingForAction: boolean;
};

/**
 * `useScheduleWeeks` for an action on one shift that reads the shift's day when it runs (the removal's schedule/actual
 * mirrors, the shifts a training status is written to): the action is never held back while the day loads, it loads
 * the day once clicked. Flag off `loadWeeks` hands over the old list at once, as before.
 */
export const useShiftDayWeeks = (date: DateTime): ShiftDayWeeks => {
	const schedule = useScheduleWeeks(date);
	const fetchScheduleWeeks = useFetchScheduleWeeks();
	const [isLoadingForAction, setIsLoadingForAction] = useState(false);
	const isReady = !schedule.isLoading && !schedule.isError;
	const loadWeeks = useCallback(async () => {
		if (isReady) return schedule.weeks;
		setIsLoadingForAction(true);
		try {
			return await fetchScheduleWeeks(date);
		} finally {
			setIsLoadingForAction(false);
		}
	}, [isReady, schedule.weeks, fetchScheduleWeeks, date]);

	return { weeks: schedule.weeks, isLoading: schedule.isLoading, isError: schedule.isError, loadWeeks, isLoadingForAction };
};
